import { config } from './config.js';
export class APIClient {
    baseUrl;
    token = null;
    refreshToken = null;
    refreshPromise = null;
    onTokenRefresh = null;
    onLogout = null;
    constructor(baseUrl) {
        // Use provided baseUrl or get from centralized config
        this.baseUrl = baseUrl || this.getDefaultBaseUrl();
        console.log('APIClient constructed with baseUrl:', this.baseUrl, 'environment:', config.env);
    }
    getDefaultBaseUrl() {
        // Check for legacy Vite environment variables for backward compatibility
        try {
            // @ts-ignore - import.meta may not be available in all environments
            if (typeof import.meta !== 'undefined' && import.meta.env) {
                // @ts-ignore
                const viteEnv = import.meta.env;
                const viteApiUrl = viteEnv.VITE_API_BASE_URL || viteEnv.VITE_API_URL;
                // If VITE_API_BASE_URL is explicitly set to empty string, use relative URLs for proxy
                if (viteEnv.VITE_API_BASE_URL === '') {
                    console.log('Using relative URLs for Vite proxy');
                    return '';
                }
                // If legacy environment variables are set, use them
                if (viteApiUrl) {
                    console.log('Using legacy Vite API URL:', viteApiUrl);
                    return viteApiUrl.replace(/\/api$/, '');
                }
            }
        }
        catch {
            // import.meta not available in this environment (CommonJS/Node.js), skip
        }
        // Use centralized config system
        const baseUrl = config.apiUrl.replace(/\/api$/, ''); // Remove /api suffix to get base URL
        console.log('Using centralized config API URL:', baseUrl, 'for environment:', config.env);
        return baseUrl;
    }
    setBaseUrl(baseUrl) {
        this.baseUrl = baseUrl;
    }
    async request(endpoint, options = {}, isRetry = false) {
        const url = `${this.baseUrl}/api${endpoint}`;
        const headers = {
            'Content-Type': 'application/json',
            ...options.headers
        };
        if (this.token) {
            headers.Authorization = `Bearer ${this.token}`;
        }
        console.log('API Request:', {
            method: options.method || 'GET',
            url,
            headers,
            baseUrl: this.baseUrl,
            isRetry
        });
        try {
            const response = await fetch(url, {
                ...options,
                headers
            });
            console.log('API Response:', {
                status: response.status,
                ok: response.ok,
                url: response.url
            });
            // Handle 401 Unauthorized - attempt token refresh if not already retrying
            if (response.status === 401 && !isRetry && this.refreshToken && endpoint !== '/auth/refresh') {
                console.log('Received 401, attempting token refresh...');
                try {
                    await this.refreshTokens();
                    // Retry the original request with the new token
                    return this.request(endpoint, options, true);
                }
                catch (refreshError) {
                    console.error('Token refresh failed, logging out:', refreshError);
                    // Token refresh failed, the performTokenRefresh method will handle logout
                    throw new Error('Session expired - please login again');
                }
            }
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }
            return await response.json();
        }
        catch (error) {
            console.error('API request failed:', {
                error: error instanceof Error ? error.message : error,
                url,
                baseUrl: this.baseUrl,
                endpoint,
                isRetry
            });
            throw error;
        }
    }
    setToken(token) {
        this.token = token;
    }
    setRefreshToken(refreshToken) {
        this.refreshToken = refreshToken;
    }
    setTokens(token, refreshToken) {
        this.token = token;
        this.refreshToken = refreshToken;
    }
    // Set callbacks for token refresh and logout
    setCallbacks(onTokenRefresh, onLogout) {
        this.onTokenRefresh = onTokenRefresh;
        this.onLogout = onLogout;
    }
    async refreshTokens() {
        if (!this.refreshToken) {
            throw new Error('No refresh token available');
        }
        // If there's already a refresh in progress, wait for it
        if (this.refreshPromise) {
            await this.refreshPromise;
            return;
        }
        this.refreshPromise = this.performTokenRefresh();
        try {
            await this.refreshPromise;
        }
        finally {
            this.refreshPromise = null;
        }
    }
    async performTokenRefresh() {
        try {
            console.log('Attempting to refresh access token...');
            const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ refreshToken: this.refreshToken })
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }
            const data = await response.json();
            this.token = data.token;
            this.refreshToken = data.refreshToken;
            console.log('Token refresh successful');
            // Notify the auth context of the new tokens
            if (this.onTokenRefresh) {
                this.onTokenRefresh(data.token, data.refreshToken);
            }
        }
        catch (error) {
            console.error('Token refresh failed:', error);
            // Clear tokens and logout
            this.token = null;
            this.refreshToken = null;
            if (this.onLogout) {
                this.onLogout();
            }
            throw error;
        }
    }
    // Auth endpoints
    async login(credentials) {
        return this.request('/auth/login', {
            method: 'POST',
            body: JSON.stringify(credentials)
        });
    }
    async register(userData) {
        return this.request('/auth/register', {
            method: 'POST',
            body: JSON.stringify(userData)
        });
    }
    async refreshAccessToken(refreshTokenValue) {
        return this.request('/auth/refresh', {
            method: 'POST',
            body: JSON.stringify({ refreshToken: refreshTokenValue })
        });
    }
    async logout(refreshTokenValue) {
        await this.request('/auth/logout', {
            method: 'POST',
            body: JSON.stringify({ refreshToken: refreshTokenValue })
        });
    }
    async verifyEmail(token) {
        return this.request('/auth/verify-email', {
            method: 'POST',
            body: JSON.stringify({ token })
        });
    }
    async resendVerificationEmail(email) {
        return this.request('/auth/resend-verification', {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    }
    async forgotPassword(data) {
        return this.request('/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }
    async resetPassword(data) {
        return this.request('/auth/reset-password', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }
    async getUserProfile() {
        return this.request('/auth/me');
    }
    // Vault endpoints
    async getCloudVaults() {
        return this.request('/vaults');
    }
    async getCloudVault(vaultId) {
        return this.request(`/vaults/${vaultId}`);
    }
    async syncVault(vaultId, data) {
        return this.request(`/vaults/${vaultId}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }
    async deleteCloudVault(vaultId) {
        await this.request(`/vaults/${vaultId}`, {
            method: 'DELETE'
        });
    }
    async getVaultStatuses(vaultIds) {
        return this.request('/vaults/status', {
            method: 'POST',
            body: JSON.stringify({ vaultIds })
        });
    }
    // Subscription endpoints
    async getSubscription() {
        return this.request('/payments/subscription');
    }
    async createSubscription(plan, paymentMethodId) {
        return this.request('/payments/subscription', {
            method: 'POST',
            body: JSON.stringify({ plan, paymentMethodId })
        });
    }
    async cancelSubscription(immediately = false) {
        return this.request('/payments/subscription', {
            method: 'DELETE',
            body: JSON.stringify({ immediately })
        });
    }
    async createBillingPortalSession() {
        return this.request('/payments/billing-portal', {
            method: 'POST'
        });
    }
    // User profile endpoints
    async updateProfile(data) {
        return this.request('/users/profile', {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }
    async changePassword(data) {
        return this.request('/users/change-password', {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }
    async updateEmail(email) {
        return this.request('/users/email', {
            method: 'PUT',
            body: JSON.stringify({ email })
        });
    }
    async submitContactForm(formData) {
        return this.request('/contact', {
            method: 'POST',
            body: JSON.stringify(formData)
        });
    }
}
export const apiClient = new APIClient();
